import { ApplicationState, ConnectedReduxProps } from '../../../../store';
import { CaseDocumentsRequest, DeleteDocumentRequest, UpdateImageRequest, createUpdateImageRequest, createCaseRequest, createDocumentRequest, createDeleteDocumentRequest } from '../../../../store/details/shared/model';
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone';
import { deleteImages, getImageSrc, replaceImages, setImagesRequestState, updateImages, uploadImages, importRevideaImages } from '../../../../store/details/images/actions';

import { CommunicationOverviewDocumentItem } from '../../../../store/details/overview/types';
import { Dispatch } from 'redux';
import { Ii18n } from '../../../../store/utils/contracts/i18n.interface';
import { createImage, Image } from '../../../../store/details/images/model';
import { ImageItem } from './image-item';
import { ImagesUIState } from '../../../../store/details/images/types';
import Lightbox from 'yet-another-react-lightbox';
import React, {createRef} from 'react';
import { connect } from 'react-redux';
import { environment } from '../../../../environment/environment';
import { getI18n } from '../../../../store/utils/i18n/strings.manager';
import styles from './images.module.css';
import { toggleIsUIDisabled } from '../../../../store/shared/actions';
import { CaseResponse } from 'src/store/case/model';
import _cloneDeep from 'lodash/cloneDeep';
import { Buffer } from 'buffer';

const Jimp = require('jimp');

type FileWithPreview = File & {
  preview: string;
};

interface PropsFromState {
  readonly currentClaimNotificationId: number;
  readonly images: Image[];
  readonly requestState: ImagesUIState;
  readonly sentDocuments: CommunicationOverviewDocumentItem[];
  readonly isUIDisabled: boolean;
  readonly caseBlocked: boolean;
  readonly instructionId: number;
  readonly selectedCase: CaseResponse;
  readonly parentPartyId: number;
}

interface PropsFromDispatch {
  readonly uploadImages: typeof uploadImages;
  readonly deleteImages: typeof deleteImages;
  readonly setImagesRequestState: typeof setImagesRequestState;
  readonly updateImages: typeof updateImages;
  readonly replaceImages: typeof replaceImages;
  readonly getImageSrc: typeof getImageSrc;
  readonly toggleIsUIDisabled: typeof toggleIsUIDisabled;
  readonly importRevideaImages: typeof importRevideaImages;
}

interface State {
  readonly isLightboxOpen: boolean;
  readonly currentImage: number;
  readonly descriptionPlaceholder: string;
  readonly currentImageDescription: string;
}

type AllProps = PropsFromState & PropsFromDispatch & ConnectedReduxProps;

class Images extends React.Component<AllProps, State> {
  private dropzoneRef: any = createRef();
  private localization: Ii18n;
  private memoizedImages: { [key: string]: React.ReactNode } = {};

  constructor(props: AllProps) {
    super(props);
    this.localization = getI18n();
    this.state = {
      isLightboxOpen: false,
      descriptionPlaceholder: this.localization.get('shared.image-description'),
      currentImageDescription: '',
      currentImage: -1
    };
  }

  isDisabled = (item: Image) => {
    if (this.props.caseBlocked) { return true; }
    if (this.props.sentDocuments.filter(doc => doc.id === item.id).length > 0) { return true; }
    return false;
  }

  openDialog = () => {
    if (this.dropzoneRef.current) {
      this.dropzoneRef.current.open();
    }
  };

  getMemoizedImages = (): React.ReactNode[] => {
    const { images } = this.props;
    const cacheKey = JSON.stringify(images); // Generate a unique cache key based on the images

    if (this.memoizedImages[cacheKey]) {
      return this.memoizedImages[cacheKey] as React.ReactNode[]; // Return cached images if they exist
    }

    // Generate and cache images if not already memoized
    const imageElements = images.map((image, index) => {
      return React.createElement(ImageItem, {
        key: index,
        disabled: this.isDisabled(image),
        filename: image.filename,
        url: image.url,
        thumbUrl: image.thumbUrl,
        isOnline: image.isOnline,
        isSelected: image.isSelected,
        isChecked: image.isChecked,
        onCheckboxChange: (ev: React.ChangeEvent, checked: boolean) =>
          this.onImageCheckedChange(index, checked),
        onPreviewClick: () => this.onPreviewClicked(index),
        onClick: () => this.onImageClicked(index),
      });
    });

    this.memoizedImages[cacheKey] = imageElements; // Cache the result
    return imageElements;
  };

  async onFilesDropped(files: FileWithPreview[]) {
    if (this.props.caseBlocked) { return; }
    this.props.toggleIsUIDisabled(true);
    for (let i = 0; i < files.length; i++) {
      if (!((files[i].name.indexOf('.jpeg') > -1 ||
      files[i].name.indexOf('.jpg') ||
      files[i].name.indexOf('.png')) &&
      files[i].type !== 'application/x-msdownload')) {
        return;
      }
    }
    const imagePromises = files.map(x => {
        return new Promise<Image>((resolve) => {
          const image = createImage();
          image.filename = x.name;
          image.url = x.preview || '';
          image.isChecked = false;

          const fileReader = new FileReader();
          fileReader.onloadend = () => {
            let c = '';
            if (typeof fileReader.result === 'string') {
              c = fileReader.result.split(',')[1] || '';
            } else if (fileReader.result instanceof ArrayBuffer) {
              const uint8Array = new Uint8Array(fileReader.result);
              const binaryString = String.fromCharCode(...uint8Array);
              c = binaryString.split(',')[1] || '';
            }
            const fc = atob(c);
            const fcf = fc.charAt(0) + fc.charAt(1);
            if (fcf !== 'MZ') {
              const buffer = Buffer.from(c , 'base64');
              Promise.resolve(this.resizeImage(buffer))
              .then((value) => {
                image.content = value.split(',')[1];
                resolve(image);
                return;
              });
              return;
            } else {
              return;
            }
          };
          fileReader.readAsDataURL(x);
          return;
        });
    });

    const droppedImages = await Promise.all(imagePromises);
    const images = [...this.props.images, ...droppedImages];
    this.props.replaceImages(images);

    this.uploadImages(images);
  }

  async resizeImage (buffer: Buffer ) {
    let jimpImage = await Jimp.Jimp.read(buffer);
    const originalHeight = jimpImage.bitmap.height;
    const originalWidth = jimpImage.bitmap.width;

    if (originalHeight > environment.maxImageHeight || originalWidth > environment.maxImageWidth) {
      jimpImage = originalHeight * environment.maxImageWidth  > originalWidth * environment.maxImageHeight
        ? await jimpImage.resize({ h: environment.maxImageHeight })
        : await jimpImage.resize({ w: environment.maxImageWidth });
    }
    const resizedImagePromise = new Promise<string>((resolve) => {
          const result = jimpImage.getBase64(jimpImage.mime);
          resolve(result);
          return;
      });
    return await Promise.resolve(resizedImagePromise);
  }

  render() {
    const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      const filesWithPreview = acceptedFiles.map(file =>
          Object.assign(file, {
              preview: URL.createObjectURL(file)
          })
      ) as FileWithPreview[];
      this.onFilesDropped(filesWithPreview);
    };

    return (
      <div style={{ height: '100%' }}>
        <div className={styles.container}>
          <div className={styles.images}>
            <Dropzone 
              ref={this.dropzoneRef}
              accept={{ 'image/*': [] }} 
              noKeyboard={true}
              noClick={true}
              onDrop={handleDrop}>
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className={styles.dropzone}>
                  <input {...getInputProps()} />
                  {this.getMemoizedImages()}
                  <div className={styles.styleDragZone}><p>Drag & Drop</p></div>
                </div>
              )}
            </Dropzone>
            <textarea
              className={styles.description}
              placeholder={this.state.descriptionPlaceholder}
              value={this.state.currentImageDescription}
              onChange={this.onDescriptionTextChange}
            />
          </div>
          <div className={styles.actionsColumn}>
            <div
              className={styles.upperButton}
              onClick={() => {
                if (this.props.caseBlocked) return;
                this.openDialog();
              }}
            >
              <img alt="" className={styles.img} src={require('../../../../assets/images/add_85x85.png')} />
            </div>
            <div
              className={styles.middleButton}
              onClick={() => {
                if (this.props.caseBlocked) return;
                this.deleteImages();
              }}
            >
              <img alt="" className={styles.img} src={require('../../../../assets/images/remove_85x85.png')} />
            </div>
            <div
              className={styles.middleButton}
              onClick={() => {
                if (this.props.caseBlocked) return;
                this.saveAllComments();
              }}
            >
              <img alt="" className={styles.img} src={require('../../../../assets/images/save_85x85.png')} />
            </div>
            <div
              className={styles.bottomButton}
              onClick={() => {
                if (this.props.caseBlocked) return;
                this.props.importRevideaImages(this.props.selectedCase.instructionId, this.props.parentPartyId);
              }}
            >
              <img alt="" className={styles.img} src={require('../../../../assets/images/retrieve_revidea_85x85.png')} />
            </div>
            <Lightbox
              close={this.onImagePreviewClose}
              index={this.state.currentImage}
              open={this.state.isLightboxOpen}
              slides={this.props.images.map((image) => ({
                src: image.url,
                caption: image.description || image.filename,
              }))}
            />
          </div>
        </div>
      </div>
    );
  }

  private uploadImages(images: Image[]) {
    if (this.props.caseBlocked) { return; }
    const imageRequests = images
      .filter(x => !x.isOnline)
      .map(x => {
        const imageRequest = createDocumentRequest();
        imageRequest.filename = x.filename;
        imageRequest.description = x.description;
        imageRequest.content = x.content;
        return imageRequest;
      });

    const request = createCaseRequest();
    request.claimNotificationId = this.props.currentClaimNotificationId;
    request.documents = imageRequests;

    this.props.uploadImages(this.props.instructionId, request);
  }

  private saveAllComments() {
    const updateImageRequests = this.props.images
      .filter((x) => x.isDirty)
      .map((x) => {
        const updateImageRequest = createUpdateImageRequest();
        updateImageRequest.id = x.id;
        updateImageRequest.description = x.description;
        updateImageRequest.filename = x.filename;
        return updateImageRequest;
      });

    this.props.updateImages(updateImageRequests);
  }

  private deleteImages() {
    const deleteImagesRequest = this.props.images
      .filter((x) => x.isOnline && x.isChecked)
      .map((x) => {
        const request = createDeleteDocumentRequest();
        request.id = x.id;
        request.amazonId = x.amazonId;
        request.thumbAmazonId = x.thumbAmazonId;

        return request;
      });

    this.props.deleteImages(deleteImagesRequest);
  }

  onImageClicked = (index: number): void => {
    if (this.state.currentImage === index) return;

    const updatedImages = this.props.images.map((image, i) =>
      i === index
        ? { ..._cloneDeep(image), isSelected: true }
        : { ..._cloneDeep(image), isSelected: false }
    );

    this.setState({
      currentImage: index,
      descriptionPlaceholder: updatedImages[index].filename,
      currentImageDescription: updatedImages[index].description || '',
    });

    this.props.replaceImages(updatedImages);
  };

  onImageCheckedChange = (index: number, checked: boolean): void => {
    // const updatedImages = this.props.images.map((image, i) =>
    //   i === index ? { ..._cloneDeep(image), isChecked: checked } : _cloneDeep(image)
    // );
    const updatedImages: Image[] = this.props.images.map((image, i) =>
      i === index ? { ...image, isChecked: checked } : { ...image }
    );
    this.props.replaceImages(updatedImages);
  };

  onPreviewClicked = (index: number): void => {
    this.props.images.forEach((image) =>
      this.props.getImageSrc(image.id, image.amazonKey, image.bucketName ? image.bucketName : 'NOTSET')
    );

    this.setState({
      currentImage: index,
      isLightboxOpen: true,
    });
  };

  onImagePreviewClose = (): void => {
    this.setState({ isLightboxOpen: false });
  };

  onDescriptionTextChange = (ev: React.ChangeEvent<HTMLTextAreaElement>): void => {
    if (this.props.caseBlocked) return;

    if (this.state.currentImage >= 0) {
      const updatedImage = {
        ..._cloneDeep(this.props.images[this.state.currentImage]),
        isDirty: this.props.images[this.state.currentImage].description !== ev.target.value,
        description: ev.target.value,
      };

      const updatedImages = this.props.images.map((image, i) =>
        i === this.state.currentImage ? updatedImage : _cloneDeep(image)
      );

      this.setState({ currentImageDescription: ev.target.value });

      this.props.replaceImages(updatedImages);
    }
  };
}

const mapStateToProps = ({ shared, caseState, details: { detailsImages, detailsOverview } }: ApplicationState): PropsFromState => ({
  currentClaimNotificationId: caseState.selectedCase.claimNotificationId,
  requestState: detailsImages.requestState,
  images: detailsImages.images,
  sentDocuments: detailsOverview.documentList,
  isUIDisabled: shared.isUIDisabled,
  parentPartyId: shared.parentPartyId,
  caseBlocked: caseState.selectedCase.blocked,
  instructionId: caseState.selectedCase.instructionId,
  selectedCase: caseState.selectedCase,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  uploadImages: (instructionId: number, request: CaseDocumentsRequest) => dispatch(uploadImages(instructionId, request)),
  setImagesRequestState: (requestState: ImagesUIState) => dispatch(setImagesRequestState(requestState)),
  deleteImages: (request: DeleteDocumentRequest[]) => dispatch(deleteImages(request)),
  updateImages: (request: UpdateImageRequest[]) => dispatch(updateImages(request)),
  replaceImages: (images: Image[]) => dispatch(replaceImages(images)),
  getImageSrc: (id: number, amazonKey: string, bucketName?: string) => dispatch(getImageSrc(id, amazonKey, bucketName)),
  toggleIsUIDisabled: (disabled: boolean) => dispatch(toggleIsUIDisabled(disabled)),
  importRevideaImages: (instructionId: number, organisationId: number) => dispatch(importRevideaImages(instructionId, organisationId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Images);
